import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/http";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, ExternalLink, Link as LinkIcon, MessageSquare, TriangleAlert, KeyRound } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { CalendarClock, Lock } from "lucide-react";
import { toast } from "sonner";

const INCLUDED_SMS = 360;

const SmsCard = () => {
  const [biz, setBiz] = useState(null);
  const [sender, setSender] = useState("");
  const [delay, setDelay] = useState(2);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/business").then(({ data }) => { setBiz(data); setSender(data.sms_sender_id || ""); setDelay(data.sms_confirmation_delay_min ?? 2); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!biz) return null;
  const enabled = !!biz.sms_enabled;
  const perMsg = Number(biz.sms_price_per_msg || 0.12);
  const save = async (patch, msg) => {
    setSaving(true);
    try { const { data } = await api.put("/business", patch); setBiz(data); if (msg) toast.success(msg); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="rounded-2xl border bg-card p-6 shadow-sm mb-6" data-testid="sms-settings-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Text messages (SMS)</h3></div>
        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${enabled ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"}`}>{enabled ? "On" : "Off"}</span>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Automated reminders, review invites and win-back texts. Messages show your clinic name as the sender.</p>

      <div className="flex items-center justify-between rounded-xl border border-border p-3 mb-4">
        <div className="text-sm font-medium">SMS {enabled ? "enabled" : "disabled"}</div>
        <Button variant={enabled ? "outline" : "default"} className="rounded-lg h-9" disabled={saving} onClick={() => save({ sms_enabled: !enabled }, enabled ? "SMS turned off" : "SMS turned on")} data-testid="sms-toggle">
          {enabled ? "Turn off" : "Turn on"}
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">Sender name (max 11 chars)</label>
          <div className="flex gap-2 mt-1">
            <Input value={sender} onChange={(e) => setSender(e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 11))} placeholder="azordi" className="h-10 rounded-lg" data-testid="sms-sender-input" />
            <Button variant="outline" className="rounded-lg h-10" disabled={saving || !sender} onClick={() => save({ sms_sender_id: sender }, "Sender name saved")} data-testid="sms-sender-save">Save</Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">Must match your registered Sender ID exactly (e.g. the ACMA/Twilio-approved <span className="font-medium">azordi</span>). An unregistered name is rejected/shown as unverified by carriers.</p>
          <div className="flex items-center justify-between rounded-lg border border-border p-2.5 mt-2" data-testid="sms-sender-status-row">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${biz.sms_sender_verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="sms-sender-status">{biz.sms_sender_verified ? "Registered · live" : "Pending registration"}</span>
              <span className="text-[11px] text-muted-foreground">{biz.sms_sender_verified ? "Real texts are sending." : "Texts are simulated until your sender ID is confirmed registered."}</span>
            </div>
            <Button variant="outline" size="sm" className="rounded-lg h-8" disabled={saving} onClick={() => save({ sms_sender_verified: !biz.sms_sender_verified }, biz.sms_sender_verified ? "Marked as pending" : "Marked as registered — real texts will send")} data-testid="sms-sender-verify-toggle">{biz.sms_sender_verified ? "Mark pending" : "Mark as registered"}</Button>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">This month's texts</label>
          <div className="mt-1 text-sm">{INCLUDED_SMS} included in your plan · extras billed at ${perMsg.toFixed(2)} each on your invoice.</div>
          <a href="/billing" className="text-xs text-primary hover:underline mt-1 inline-block" data-testid="sms-see-billing">See usage in Billing →</a>
        </div>
      </div>
      <div className="rounded-xl border border-border p-3 mb-4" data-testid="sms-delay-row">
        <label className="text-xs uppercase tracking-wider text-muted-foreground">Confirmation grace period</label>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <Input type="number" min="0" max="60" value={delay} onChange={(e) => setDelay(e.target.value)} className="h-10 w-20 rounded-lg tabular-nums" data-testid="sms-delay-input" />
          <span className="text-sm text-muted-foreground">minutes</span>
          <Button variant="outline" className="rounded-lg h-10" disabled={saving} onClick={() => save({ sms_confirmation_delay_min: Math.max(0, parseInt(delay) || 0) }, "Grace period saved")} data-testid="sms-delay-save">Save</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Booking confirmation &amp; reschedule texts wait this long before sending. If you fix the time or delete a booking within the window, the client only gets one text with the correct details — or none if it was a mistake. Set to <span className="font-medium">0</span> to send right away.</p>
      </div>
      <p className="text-xs text-muted-foreground">Delivery uses Azordi's messaging platform — no Twilio account needed. If your region requires sender-ID registration, real delivery starts once that's approved (until then sends are logged as simulated).</p>
    </Card>
  );
};

const RepairPricesCard = () => {
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(null);
  const run = async () => {
    if (!window.confirm("Fix recurring prices?\n\nThis copies each recurring appointment's original price onto all its future (not-yet-completed) dates so they match. Safe to run.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/bookings/repair-series-prices");
      setDone(data);
      toast.success(`Fixed ${data.bookings_updated} appointment${data.bookings_updated === 1 ? "" : "s"} across ${data.series_fixed} series`);
    } catch { toast.error("Couldn't run the price repair"); } finally { setRunning(false); }
  };
  return (
    <Card className="rounded-2xl border bg-card p-6 shadow-sm mb-6" data-testid="repair-prices-card">
      <h3 className="font-heading font-semibold mb-2">Fix recurring prices</h3>
      <p className="text-sm text-muted-foreground mb-3">If some repeating appointments show the standard service price instead of a manual price you set, run this once. It copies each series' original price onto all its upcoming dates. Completed/paid appointments are left untouched.</p>
      <Button variant="outline" className="rounded-lg" disabled={running} onClick={run} data-testid="repair-prices-btn">{running ? "Fixing…" : "Fix recurring prices"}</Button>
      {done && <p className="text-xs text-muted-foreground mt-2" data-testid="repair-prices-result">Updated {done.bookings_updated} appointment(s) across {done.series_fixed} series.</p>}
    </Card>
  );
};

const OnlineBookingCard = () => {
  const [allow, setAllow] = useState(true);
  const [payLater, setPayLater] = useState(true);
  const [interval, setIntervalVal] = useState("30");
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/business").then(({ data }) => { setAllow(data.allow_same_day_booking !== false); setPayLater(data.deposit_pay_later !== false); setIntervalVal(String(data.slot_interval_min || 30)); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);
  const togglePayLater = async (v) => {
    setPayLater(v);
    try { await api.put("/business", { deposit_pay_later: v }); toast.success(v ? "Clients can choose to pay their deposit later" : "Deposits must now be paid to book online"); }
    catch (e) { setPayLater(!v); toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
  };
  const toggle = async (v) => {
    setAllow(v);
    try { await api.put("/business", { allow_same_day_booking: v }); toast.success(v ? "Same-day online bookings allowed" : "Same-day online bookings turned off"); }
    catch (e) { setAllow(!v); toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
  };
  const saveInterval = async (v) => {
    setIntervalVal(v);
    try { await api.put("/business", { slot_interval_min: parseInt(v, 10) }); toast.success(`Times now offered every ${v} minutes`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
  };
  return (
    <Card className="rounded-2xl border bg-card p-6 shadow-sm mb-6" data-testid="online-booking-card">
      <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" />Online booking</h3>
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-medium">Allow same-day bookings</div>
          <p className="text-xs text-muted-foreground mt-0.5">When off, clients can't book online for today — the earliest they can choose is tomorrow. You can still add same-day bookings yourself in the diary.</p>
        </div>
        <Switch checked={allow} onCheckedChange={toggle} disabled={!loaded} data-testid="allow-same-day-toggle" />
      </div>
      <div className="border-t border-border/60 mt-4 pt-4 flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-medium">Let clients pay the deposit later</div>
          <p className="text-xs text-muted-foreground mt-0.5">Adds a "Book now, pay deposit later" option online. The booking is confirmed straight away, the client is texted a payment link (and a reminder a day later), and the diary shows a "Deposit unpaid" badge until it's paid. Nothing is auto-cancelled.</p>
        </div>
        <Switch checked={payLater} onCheckedChange={togglePayLater} disabled={!loaded} data-testid="deposit-pay-later-toggle" />
      </div>
      <div className="border-t border-border/60 mt-4 pt-4 flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-medium">Time slot spacing</div>
          <p className="text-xs text-muted-foreground mt-0.5">How far apart the bookable start times appear (e.g. 09:00, 09:30…). Wider spacing looks tidier and reduces small gaps.</p>
        </div>
        <Select value={interval} onValueChange={saveInterval} disabled={!loaded}>
          <SelectTrigger className="h-10 rounded-lg w-32 shrink-0" data-testid="slot-interval-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="15">Every 15 min</SelectItem>
            <SelectItem value="20">Every 20 min</SelectItem>
            <SelectItem value="30">Every 30 min</SelectItem>
            <SelectItem value="60">Every 60 min</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </Card>
  );
};


const PasscodeLockCard = () => {
  const { business, setBusiness } = useAuth();
  const [mins, setMins] = useState(String(business?.auto_lock_minutes ?? 10));
  useEffect(() => { setMins(String(business?.auto_lock_minutes ?? 10)); }, [business?.auto_lock_minutes]);
  const save = async (v) => {
    setMins(v);
    try { const { data } = await api.put("/business", { auto_lock_minutes: parseInt(v, 10) }); setBusiness?.(data); toast.success(v === "0" ? "Passcode lock turned off" : `Locks after ${v} minutes idle`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
  };
  return (
    <Card className="rounded-2xl border bg-card p-6 shadow-sm mb-6" data-testid="passcode-lock-card">
      <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Lock className="h-4 w-4 text-primary" />Passcode lock</h3>
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-medium">Lock the screen when idle</div>
          <p className="text-xs text-muted-foreground mt-0.5">After this long with no activity, staff must enter their 4-digit PIN to continue. Turn it off if you're the only person using the device. Staff can still switch users with their PIN from the sidebar.</p>
        </div>
        <Select value={mins} onValueChange={save}>
          <SelectTrigger className="h-10 rounded-lg w-36 shrink-0" data-testid="passcode-lock-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Off</SelectItem>
            <SelectItem value="5">After 5 min</SelectItem>
            <SelectItem value="10">After 10 min</SelectItem>
            <SelectItem value="30">After 30 min</SelectItem>
            <SelectItem value="60">After 1 hour</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </Card>
  );
};

const HistoricalBaselineCard = () => {
  const { business } = useAuth();
  const cur = business?.currency || "AUD";
  const [b, setB] = useState({ retention_rate: "", rebook_rate: "", avg_value: "", avg_visits: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/business").then(({ data }) => {
      const h = data.historical_baseline || {};
      setB({ retention_rate: h.retention_rate ?? "", rebook_rate: h.rebook_rate ?? "", avg_value: h.avg_value ?? "", avg_visits: data.avg_visits ?? "" });
    }).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/business", { historical_baseline: {
        retention_rate: parseFloat(b.retention_rate) || 0,
        rebook_rate: parseFloat(b.rebook_rate) || 0,
        avg_value: parseFloat(b.avg_value) || 0,
      }, avg_visits: parseFloat(b.avg_visits) || 0 });
      toast.success("Historical baseline saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="rounded-2xl border bg-card p-6 shadow-sm mb-6" data-testid="historical-baseline-card">
      <h3 className="font-heading font-semibold mb-2">Historical figures (from your old system)</h3>
      <p className="text-sm text-muted-foreground mb-4">Enter your usual retention, rebook and average spend. Until this system has a full previous month of its own data, the dashboard uses these as the "previous" comparison so the up/down arrows are meaningful from day one. Once real history builds up, it automatically switches to your actual month-over-month figures.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Client retention</label>
          <div className="relative"><Input type="number" min="0" max="100" value={b.retention_rate} onChange={(e) => setB((s) => ({ ...s, retention_rate: e.target.value }))} className="h-11 rounded-lg pr-7 tabular-nums" data-testid="baseline-retention" /><span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span></div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Rebook rate</label>
          <div className="relative"><Input type="number" min="0" max="100" value={b.rebook_rate} onChange={(e) => setB((s) => ({ ...s, rebook_rate: e.target.value }))} className="h-11 rounded-lg pr-7 tabular-nums" data-testid="baseline-rebook" /><span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span></div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Average spend</label>
          <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span><Input type="number" min="0" value={b.avg_value} onChange={(e) => setB((s) => ({ ...s, avg_value: e.target.value }))} className="h-11 rounded-lg pl-7 tabular-nums" data-testid="baseline-avg" /></div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Avg. visits per client</label>
          <Input type="number" min="0" step="0.5" value={b.avg_visits} onChange={(e) => setB((s) => ({ ...s, avg_visits: e.target.value }))} className="h-11 rounded-lg tabular-nums" placeholder="e.g. 6" data-testid="baseline-avg-visits" />
          <p className="text-[11px] text-muted-foreground mt-1">Used to estimate each client's lifetime value in the New clients report.</p>
        </div>
      </div>
      <Button className="rounded-lg mt-4" disabled={saving} onClick={save} data-testid="baseline-save">{saving ? "Saving…" : "Save baseline"}</Button>
    </Card>
  );
};


const ReviewRequestsCard = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/surveys/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return null;
  const save = async () => {
    try { const { data } = await api.put("/surveys/settings", { auto_send: s.auto_send, trigger_visit: Number(s.trigger_visit) || 3, google_review_url: s.google_review_url || "", min_score_for_google: s.min_score_for_google }); setS(data); toast.success("Review requests saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
  };
  return (
    <Card className="rounded-2xl border bg-card p-6 shadow-sm" data-testid="review-requests-card">
      <h3 className="font-heading font-semibold mb-1">Google review requests</h3>
      <p className="text-sm text-muted-foreground mb-4">After a client's visit #{s.trigger_visit} they get a quick rating text; happy clients are sent on to your Google review link.</p>
      <div className="grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
        <div><label className="text-xs text-muted-foreground">Google review link</label><Input value={s.google_review_url || ""} onChange={(e) => setS({ ...s, google_review_url: e.target.value })} placeholder="https://g.page/r/…/review" className="h-10 rounded-lg mt-1" data-testid="review-google-url" /></div>
        <div><label className="text-xs text-muted-foreground">Ask after visit #</label><Input type="number" min="1" max="50" value={s.trigger_visit} onChange={(e) => setS({ ...s, trigger_visit: e.target.value })} className="h-10 rounded-lg mt-1 w-24" data-testid="review-trigger-visit" /></div>
        <label className="flex items-center gap-2 text-sm h-10"><Switch checked={!!s.auto_send} onCheckedChange={(v) => setS({ ...s, auto_send: v })} data-testid="review-auto-send" />Send automatically</label>
      </div>
      <div className="mt-3 flex justify-end"><Button onClick={save} className="rounded-lg" data-testid="review-save">Save</Button></div>
    </Card>
  );
};

const ChangePasswordCard = () => {
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (pw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pw !== confirm) { toast.error("New passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Password changed");
      setCur(""); setPw(""); setConfirm("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't change password"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="rounded-2xl border bg-card p-6 shadow-sm mb-6" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-3"><KeyRound className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Change password</h3></div>
      <p className="text-sm text-muted-foreground mb-4">Update the password for your own login.</p>
      <div className="space-y-3 max-w-sm">
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">Current password</label>
          <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="h-11 rounded-lg mt-1" autoComplete="current-password" data-testid="cp-current" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">New password</label>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 rounded-lg mt-1" autoComplete="new-password" data-testid="cp-new" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">Confirm new password</label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11 rounded-lg mt-1" autoComplete="new-password" data-testid="cp-confirm" />
        </div>
        <Button className="rounded-lg" disabled={busy || !cur || !pw || !confirm} onClick={save} data-testid="cp-save">{busy ? "Saving…" : "Update password"}</Button>
      </div>
    </Card>
  );
};


export default function Settings() {
  const { business, user, logout } = useAuth();
  const navigate = useNavigate();
  const bookingUrl = `${window.location.origin}/book/${business?.slug}`;
  const [copied, setCopied] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [needsReassign, setNeedsReassign] = useState(0);
  const [reassignTo, setReassignTo] = useState("");
  const [closeOpen, setCloseOpen] = useState(false);
  const [closePw, setClosePw] = useState("");
  const [closeConfirm, setCloseConfirm] = useState("");
  const [closing, setClosing] = useState(false);
  const [permOpen, setPermOpen] = useState(false);
  const [permPw, setPermPw] = useState("");
  const [permConfirm, setPermConfirm] = useState("");
  const [permBusy, setPermBusy] = useState(false);
  const isOwner = user?.role === "owner";

  useEffect(() => { if (!isOwner) api.get("/staff").then(({ data }) => setStaffList((data || []).filter((s) => s.id !== user?.id))).catch(() => {}); }, [isOwner, user?.id]);

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/me", { data: { password: delPw } });
      toast.success("Your account has been deactivated. Sign back in any time to reactivate — nothing is lost.");
      logout();
      navigate("/login");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't deactivate your account");
    } finally { setDeleting(false); }
  };

  const closeClinic = async () => {
    setClosing(true);
    try {
      await api.delete("/auth/me", { data: { password: closePw } });
      toast.success("Your clinic has been deactivated. Sign back in any time to reactivate — all records are kept.");
      logout();
      navigate("/login");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't deactivate the clinic");
    } finally { setClosing(false); }
  };

  const permanentDelete = async () => {
    setPermBusy(true);
    try {
      await api.delete("/auth/me", { data: { password: permPw, permanent: true } });
      toast.success(isOwner ? "Your clinic and all data have been permanently deleted." : "Your account has been permanently deleted.");
      logout();
      navigate("/login");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete");
    } finally { setPermBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(bookingUrl);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = bookingUrl; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch (e) { console.warn("Clipboard copy fallback failed", e); }
      document.body.removeChild(ta);
    }
    setCopied(true); toast.success("Booking link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight mb-6">Settings</h1>

      <Card className="rounded-2xl border bg-card p-6 shadow-sm mb-6">
        <h3 className="font-heading font-semibold mb-4">Business</h3>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div><div className="text-muted-foreground text-xs uppercase tracking-wider">Name</div><div className="font-medium">{business?.name}</div></div>
          <div><div className="text-muted-foreground text-xs uppercase tracking-wider">Currency</div><div className="font-medium">{business?.currency}</div></div>
          <div><div className="text-muted-foreground text-xs uppercase tracking-wider">Owner</div><div className="font-medium">{user?.name} ({user?.email})</div></div>
          <div><div className="text-muted-foreground text-xs uppercase tracking-wider">Booking slug</div><div className="font-medium">{business?.slug}</div></div>
        </div>
      </Card>

      <Card className="rounded-2xl border bg-card p-6 shadow-sm mb-6">
        <div className="flex items-center gap-2 mb-3"><LinkIcon className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Online booking link</h3></div>
        <p className="text-sm text-muted-foreground mb-3">Share this link so clients can book online 24/7.</p>
        <div className="flex gap-2">
          <Input readOnly value={bookingUrl} className="h-11 rounded-lg font-mono text-sm" data-testid="booking-link" />
          <Button variant="outline" className="rounded-lg" onClick={copy} data-testid="copy-booking-link"><Copy className="h-4 w-4 mr-1.5" />{copied ? "Copied" : "Copy"}</Button>
          <a href={bookingUrl} target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-lg" data-testid="open-booking-link"><ExternalLink className="h-4 w-4" /></Button></a>
        </div>
      </Card>

      <SmsCard />

      <ReviewRequestsCard />

      <ChangePasswordCard />

      {isOwner && <RepairPricesCard />}

      {isOwner && <HistoricalBaselineCard />}

      {isOwner && <OnlineBookingCard />}

      {isOwner && <PasscodeLockCard />}

      <Card className="rounded-2xl border bg-card p-6 shadow-sm">
        <h3 className="font-heading font-semibold mb-2">More tools</h3>
        <p className="text-sm text-muted-foreground">Consult forms, treatment charts, aftercare, Meta ads, Finance and Reports are all available from the sidebar and Setup menu. Marketing integrations (Mailchimp, Klaviyo) live under Integrations.</p>
      </Card>

      <Card className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 shadow-sm mt-6" data-testid="danger-zone">
        {isOwner ? (
          <>
            <div className="flex items-center gap-2 mb-2"><TriangleAlert className="h-4 w-4 text-destructive" /><h3 className="font-heading font-semibold text-destructive">Deactivate clinic</h3></div>
            {!closeOpen ? (
              <>
                <p className="text-sm text-muted-foreground mb-3">Deactivate <b>{business?.name}</b>. All your records are kept and login is paused for your whole team. You can reactivate any time by signing back in with your owner login. To just stop being charged, cancel your subscription in Billing instead.</p>
                <Button variant="destructive" className="rounded-lg" onClick={() => setCloseOpen(true)} data-testid="close-clinic-start">Deactivate clinic</Button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-3">Enter your password to deactivate <b>{business?.name}</b>. Your data is kept — sign back in any time to reactivate.</p>
                <div className="space-y-2 max-w-md">
                  <Input type="password" placeholder="Your password" value={closePw} onChange={(e) => setClosePw(e.target.value)} className="h-11 rounded-lg" data-testid="close-clinic-password" />
                  <div className="flex gap-2">
                    <Button variant="destructive" className="rounded-lg" disabled={!closePw || closing} onClick={closeClinic} data-testid="close-clinic-confirm">{closing ? "Deactivating…" : "Deactivate clinic"}</Button>
                    <Button variant="outline" className="rounded-lg" onClick={() => { setCloseOpen(false); setClosePw(""); }} data-testid="close-clinic-cancel">Cancel</Button>
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2"><TriangleAlert className="h-4 w-4 text-destructive" /><h3 className="font-heading font-semibold text-destructive">Deactivate my account</h3></div>
            {!delOpen ? (
              <>
                <p className="text-sm text-muted-foreground mb-3">Deactivate your staff login. Your profile and records are kept, and you can reactivate any time by signing back in — nothing is lost.</p>
                <Button variant="destructive" className="rounded-lg" onClick={() => setDelOpen(true)} data-testid="delete-account-start">Deactivate my account</Button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-3">Enter your password to deactivate. You can reactivate any time by signing back in — nothing is lost.</p>
                <div className="flex flex-col sm:flex-row gap-2 max-w-md">
                  <Input type="password" placeholder="Your password" value={delPw} onChange={(e) => setDelPw(e.target.value)} className="h-11 rounded-lg" data-testid="delete-account-password" />
                  <Button variant="destructive" className="rounded-lg shrink-0" disabled={!delPw || deleting} onClick={deleteAccount} data-testid="delete-account-confirm">{deleting ? "Deactivating…" : "Deactivate"}</Button>
                  <Button variant="outline" className="rounded-lg shrink-0" onClick={() => { setDelOpen(false); setDelPw(""); }} data-testid="delete-account-cancel">Cancel</Button>
                </div>
              </>
            )}
          </>
        )}
      </Card>

      <Card className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 shadow-sm mt-4" data-testid="permanent-delete-zone">
        <div className="flex items-center gap-2 mb-2"><TriangleAlert className="h-4 w-4 text-destructive" /><h3 className="font-heading font-semibold text-destructive">Permanently delete {isOwner ? "clinic & all data" : "my account"}</h3></div>
        {!permOpen ? (
          <>
            <p className="text-sm text-muted-foreground mb-3">
              {isOwner
                ? <>Irreversibly erase <b>{business?.name}</b> and <b>everything</b> — bookings, clients, staff, messages, sales and settings. This <b>cannot be undone</b> and data cannot be recovered. Use "Deactivate" above if you only want to pause access.</>
                : <>Irreversibly delete your staff login and personal profile. This <b>cannot be undone</b>. Use "Deactivate" above if you only want to pause your login.</>}
            </p>
            <Button variant="outline" className="rounded-lg border-destructive/60 text-destructive hover:bg-destructive/10" onClick={() => setPermOpen(true)} data-testid="perm-delete-start">Permanently delete {isOwner ? "everything" : "my account"}</Button>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-3">This permanently erases {isOwner ? <>all data for <b>{business?.name}</b></> : "your account"} and cannot be undone.{isOwner && <> Type your clinic name and password to confirm.</>}{!isOwner && " Enter your password to confirm."}</p>
            <div className="space-y-2 max-w-md">
              {isOwner && <Input placeholder={`Type "${business?.name}" to confirm`} value={permConfirm} onChange={(e) => setPermConfirm(e.target.value)} className="h-11 rounded-lg" data-testid="perm-delete-confirm-name" />}
              <Input type="password" placeholder="Your password" value={permPw} onChange={(e) => setPermPw(e.target.value)} className="h-11 rounded-lg" data-testid="perm-delete-password" />
              <div className="flex gap-2">
                <Button variant="destructive" className="rounded-lg" disabled={!permPw || permBusy || (isOwner && (permConfirm || "").trim().toLowerCase() !== (business?.name || "").trim().toLowerCase())} onClick={permanentDelete} data-testid="perm-delete-confirm">{permBusy ? "Deleting…" : "Permanently delete"}</Button>
                <Button variant="outline" className="rounded-lg" onClick={() => { setPermOpen(false); setPermPw(""); setPermConfirm(""); }} data-testid="perm-delete-cancel">Cancel</Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
